/**
 * Ledger Entry Editor - Dirty State Detection
 *
 * Implements per-field dirty detection by comparing draft values against
 * original snapshot. Supports capability filtering to only track fields
 * that are enabled for editing.
 *
 * Dirty detection is field-level: a field is dirty if its current value
 * differs from the snapshot value. Unchanged fields are omitted from
 * mutation payloads.
 */

/**
 * Capability flags determine which fields are eligible for dirty detection.
 * Only enabled capabilities contribute to dirty state.
 */
export type EditorCapabilities = {
	canEditEntry?: boolean;
	canEditAssociation?: boolean;
	canEditParties?: boolean;
	canEditLinkedEntry?: boolean;
	canTogglePosted?: boolean;
	canDeleteRole?: boolean;
	canDeleteEntry?: boolean;
};

/**
 * Snapshot of original entry/accounting data.
 * Used as baseline for dirty detection.
 */
export type EntrySnapshot = {
	orderId?: string;
	entryId?: string;
	accountingId?: string;
	amount?: number;
	code?: string;
	description?: string;
	kind?: string;
	entryDate?: string | null;
	letter?: string | null;
	number?: number | null;
	months?: number | null;
	months_advanced?: number | null;
	annuality?: number | null;
	posted?: boolean;
	[key: string]: unknown;
};

/**
 * Draft form values (from react-hook-form).
 * May contain partial or modified values.
 */
export type EntryDraft = {
	orderId?: string;
	entryId?: string;
	accountingId?: string;
	amount?: number;
	code?: string;
	description?: string;
	kind?: string;
	entryDate?: string | null;
	letter?: string | null;
	number?: number | null;
	months?: number | null;
	months_advanced?: number | null;
	annuality?: number | null;
	posted?: boolean;
	// List fields for parties, associations, links
	involved_parties?: Array<{
		_id?: string;
		name?: string;
		kind?: string;
		amount?: number;
		percent?: number;
	}>;
	[key: string]: unknown;
};

/**
 * Per-field dirty state.
 * Maps field name to boolean indicating if it differs from snapshot.
 */
export type DirtyFields = {
	[fieldName: string]: boolean;
};

/**
 * Deep clone helper for snapshot creation.
 * Handles primitives, objects, arrays, and null/undefined.
 */
export function deepClone<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(item => deepClone(item)) as T;
  }

  const cloned = {} as T;
  Object.keys(value).forEach(key => {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      (cloned as Record<string, unknown>)[key] = deepClone((value as Record<string, unknown>)[key]);
    }
  });
  return cloned;
}

/**
 * Create a snapshot of current entry data.
 * Snapshot is a deep clone to prevent external mutations.
 */
export function createSnapshot(entry: EntryDraft): EntrySnapshot {
  return deepClone(entry);
}

/**
 * Compare two values for equality.
 * Handles primitives, objects, arrays, null, and undefined.
 */
function valuesEqual(a: unknown, b: unknown): boolean {
  // Strict equality for primitives
  if (a === b) {
    return true;
  }

  // Both null or both undefined
  if ((a === null && b === null) || (a === undefined && b === undefined)) {
    return true;
  }

  // One is null/undefined, other is not
  if ((a === null || a === undefined) !== (b === null || b === undefined)) {
    return false;
  }

  // Both are objects (including arrays)
  if (typeof a === 'object' && typeof b === 'object') {
    // Array comparison
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return false;
      }
      return a.every((item, idx) => valuesEqual(item, b[idx]));
    }

    // Object comparison
    if (!Array.isArray(a) && !Array.isArray(b)) {
      const keysA = Object.keys(a as Record<string, unknown>);
      const keysB = Object.keys(b as Record<string, unknown>);

      if (keysA.length !== keysB.length) {
        return false;
      }

      return keysA.every(key => valuesEqual(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key],
      ));
    }
  }

  return false;
}

/**
 * Detect dirty fields by comparing draft against snapshot.
 * Only fields present in capabilities are checked.
 *
 * @param snapshot Original entry data
 * @param draft Current form values
 * @param capabilities Which fields are enabled for editing
 * @returns Map of field name to dirty boolean
 */
export function detectDirtyFields(
  snapshot: EntrySnapshot,
  draft: EntryDraft,
  capabilities: EditorCapabilities = {},
): DirtyFields {
  const dirty: DirtyFields = {};

  // Define which fields belong to each capability
  const capabilityFields: Record<string, string[]> = {
    canEditEntry: ['amount', 'code', 'description', 'kind', 'entryDate', 'letter', 'number', 'months', 'months_advanced', 'annuality'],
    canEditAssociation: ['associationData'],
    canEditParties: ['involved_parties'],
    canEditLinkedEntry: ['linkedEntry'],
    canTogglePosted: ['posted'],
    canDeleteRole: ['role'],
    canDeleteEntry: ['deleteEntry'],
  };

  // Collect all fields that should be checked based on capabilities
  const fieldsToCheck = new Set<string>();
  Object.entries(capabilityFields).forEach(([capability, fields]) => {
    if (capabilities[capability as keyof EditorCapabilities]) {
      fields.forEach(f => fieldsToCheck.add(f));
    }
  });

  // If no capabilities enabled, nothing is dirty
  if (fieldsToCheck.size === 0) {
    return dirty;
  }

  // Check each field
  Array.from(fieldsToCheck).forEach(field => {
    const snapshotValue = snapshot[field];
    const draftValue = draft[field];

    // Compare values: use strict equality for primitives, deep comparison for objects
    dirty[field] = !valuesEqual(snapshotValue, draftValue);
  });

  return dirty;
}

/**
 * Get all dirty field names.
 */
export function getDirtyFieldNames(dirtyFields: DirtyFields): string[] {
  return Object.keys(dirtyFields).filter(field => dirtyFields[field]);
}

/**
 * Check if any field is dirty.
 */
export function hasAnyDirtyFields(dirtyFields: DirtyFields): boolean {
  return getDirtyFieldNames(dirtyFields).length > 0;
}

/**
 * Build a payload containing only dirty fields.
 * Omits unchanged fields entirely.
 */
export function buildDirtyPayload(
  draft: EntryDraft,
  dirtyFields: DirtyFields,
): Partial<EntryDraft> {
  const payload: Partial<EntryDraft> = {};

  getDirtyFieldNames(dirtyFields).forEach(field => {
    payload[field] = draft[field];
  });

  return payload;
}

/**
 * Merge server response values into snapshot and draft.
 * Updates snapshot to reflect server state, marks fields clean.
 */
export function mergeServerResponse(
  snapshot: EntrySnapshot,
  draft: EntryDraft,
  response: Record<string, unknown>,
): { snapshot: EntrySnapshot; draft: EntryDraft } {
  const updatedSnapshot = deepClone(snapshot);
  const updatedDraft = deepClone(draft);

  // Merge response into both snapshot and draft
  Object.entries(response).forEach(([key, value]) => {
    (updatedSnapshot as Record<string, unknown>)[key] = value;
    (updatedDraft as Record<string, unknown>)[key] = value;
  });

  return { snapshot: updatedSnapshot, draft: updatedDraft };
}
